import type {
  BoundaryFrame,
  PerpendicularLine,
  Vec3,
  VoxelIJKChange,
} from './markerLineTypes';

export const mergeVoxelChanges = (...groups: VoxelIJKChange[][]) => {
  const merged = new Map<string, VoxelIJKChange>();
  groups.forEach((group) => {
    group.forEach((v) => {
      merged.set(`${v.i}:${v.j}:${v.k}:${v.value}`, v);
    });
  });
  return Array.from(merged.values());
};

export const getPolyPoints = ({
  pickPoints,
  perpendicularLines,
  numPairs,
}: {
  pickPoints: number[];
  perpendicularLines: PerpendicularLine[];
  numPairs: number;
}) => {
  const res: string[] = [];
  if (!perpendicularLines.length) return res;
  if (numPairs === 1) return res;

  for (let i = 0; i < pickPoints.length; i += numPairs) {
    const proximal = pickPoints[i];
    const distal = pickPoints[i + numPairs - 1];

    if (
      proximal >= perpendicularLines.length ||
      distal >= perpendicularLines.length
    ) {
      continue;
    }

    let pts = '';
    for (let j = proximal; j <= distal; ++j) {
      const { startX, startY } = perpendicularLines[j];
      pts += `${startX},${startY} `;
    }

    for (let j = distal; j >= proximal; --j) {
      const { endX, endY } = perpendicularLines[j];
      pts += `${endX},${endY}${j > proximal ? ' ' : ''}`;
    }
    res.push(pts);
  }
  return res;
};

export const clampValue = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

export const normalizeVec3 = (value: Vec3, fallback: Vec3): Vec3 => {
  const len = Math.hypot(value[0], value[1], value[2]);
  if (!Number.isFinite(len) || len < 1e-6) return fallback;
  return [value[0] / len, value[1] / len, value[2] / len];
};

export const crossVec3 = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

export const getVoxelKey = (voxel: Pick<VoxelIJKChange, 'i' | 'j' | 'k'>) =>
  `${voxel.i}:${voxel.j}:${voxel.k}`;

export const createRawLumenMaskFromLabelMap = (labelMap: {
  width: number;
  height: number;
  data: Uint8Array;
}) => {
  const mask = new Uint8Array(labelMap.width * labelMap.height);
  const n = Math.min(mask.length, labelMap.data.length);
  for (let i = 0; i < n; i += 1) {
    mask[i] = labelMap.data[i] === 255 ? 255 : 0;
  }
  return mask;
};

export const areMasksEqual = (a?: Uint8Array | null, b?: Uint8Array | null) => {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export const findCenterRunInMaskRow = (
  mask: Uint8Array | null | undefined,
  width: number,
  height: number,
  row: number,
  centerX: number,
): { left: number; right: number } | null => {
  if (!mask || row < 0 || row >= height) return null;

  const offset = row * width;
  let x = 0;
  let bestContaining: { left: number; right: number } | null = null;
  let bestContainingLen = -1;
  let best: { left: number; right: number } | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  while (x < width) {
    while (x < width && mask[offset + x] === 0) x += 1;
    if (x >= width) break;
    const left = x;
    while (x < width && mask[offset + x] > 0) x += 1;
    const right = x - 1;
    const len = right - left + 1;
    const cx = (left + right) * 0.5;
    const containsCenter = left <= centerX && centerX <= right;

    if (containsCenter && len > bestContainingLen) {
      bestContainingLen = len;
      bestContaining = { left, right };
    }

    const score = Math.abs(cx - centerX) - len * 0.15;
    if (score < bestScore) {
      bestScore = score;
      best = { left, right };
    }
  }

  return bestContaining ?? best;
};

export const getChangedBounds = (
  changed: Iterable<number>,
  frame: BoundaryFrame,
  paddingPx: number,
) => {
  const indices = Array.from(changed);
  if (!indices.length) return null;

  let minRow = Number.POSITIVE_INFINITY;
  let maxRow = Number.NEGATIVE_INFINITY;
  let minCol = Number.POSITIVE_INFINITY;
  let maxCol = Number.NEGATIVE_INFINITY;

  for (const idx of indices) {
    if (!Number.isFinite(idx) || idx < 0) continue;
    const row = Math.floor(idx / frame.width);
    const col = idx - row * frame.width;
    if (row < 0 || row >= frame.rows.length || col < 0 || col >= frame.width) {
      continue;
    }
    minRow = Math.min(minRow, row);
    maxRow = Math.max(maxRow, row);
    minCol = Math.min(minCol, col);
    maxCol = Math.max(maxCol, col);
  }

  if (![minRow, maxRow, minCol, maxCol].every(Number.isFinite)) return null;

  const pad = Math.max(0, Math.floor(paddingPx));
  return {
    rowStart: Math.max(0, minRow - pad),
    rowEnd: Math.min(frame.rows.length - 1, maxRow + pad),
    colStart: Math.max(0, minCol - pad),
    colEnd: Math.min(frame.width - 1, maxCol + pad),
    changedSeedCount2D: indices.length,
  };
};
